// ========================================
// pages/Beneficiaries.js - External Beneficiary Management
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Manage external beneficiaries (asset-level beneficiaries)
// 
// WHAT ARE EXTERNAL BENEFICIARIES?
// - Beneficiaries added PER ASSET (not shared across all assets like Global NOKs)
// - Plan limits: Premium (1 per asset), Business (2), Enterprise (3)
// - Free plan cannot use external beneficiaries
// - Can be deleted entirely (no restrictions)
// 
// FEATURES:
// - View all external beneficiaries
// - Add new external beneficiary
// - Delete beneficiary (with orphaned asset confirmation)
// - View assets assigned to a beneficiary
// 
// ASSETS USED:
// - /images/icons/email-icon.png - Email icon (using emoji as fallback)
// - /images/icons/phone-icon.png - Phone icon (using emoji as fallback)
// - Asset icons from /images/assets/ for asset list
// 
// RESPONSIVE BREAKPOINTS:
// - Desktop (>1024px): 2 column grid
// - Tablet (768px-1024px): 2 column grid with adjusted padding
// - Mobile (<768px): 1 column stacked layout
// 
// FOOTER: Included (full width, proper spacing)
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import Footer from '../components/Footer';

const Beneficiaries = () => {
  const navigate = useNavigate();
  
  // ========================================
  // STATE VARIABLES
  // ========================================
  const [beneficiaries, setBeneficiaries] = useState([]);      // List of external beneficiaries
  const [loading, setLoading] = useState(true);                // Loading state
  const [error, setError] = useState('');                      // Error message
  const [showForm, setShowForm] = useState(false);             // Show add form
  const [formData, setFormData] = useState({                   // Form data for new beneficiary
    name: '',
    email: '',
    phone: '',
    relationship: 'other'
  });
  const [submitting, setSubmitting] = useState(false);         // Submit state
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);  // Selected beneficiary for asset view
  const [beneficiaryAssets, setBeneficiaryAssets] = useState([]);        // Assets for selected beneficiary
  const [assetsLoading, setAssetsLoading] = useState(false);   // Assets loading state

  // ========================================
  // FETCH BENEFICIARIES ON COMPONENT MOUNT
  // ========================================
  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const fetchBeneficiaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/beneficiaries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBeneficiaries(response.data);
    } catch (err) {
      setError('Failed to load beneficiaries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // HANDLE FORM INPUT CHANGES
  // ========================================
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // ========================================
  // ADD NEW EXTERNAL BENEFICIARY
  // ========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/beneficiaries`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Reset form and refresh list
      setFormData({
        name: '',
        email: '',
        phone: '',
        relationship: 'other'
      });
      setShowForm(false);
      fetchBeneficiaries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create beneficiary');
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================
  // VIEW ASSETS FOR SELECTED BENEFICIARY
  // ========================================
  const handleViewAssets = async (beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setAssetsLoading(true);
    setBeneficiaryAssets([]);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/beneficiaries/${beneficiary.id}/assets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBeneficiaryAssets(response.data);
    } catch (err) {
      console.error('Failed to load assets:', err);
    } finally {
      setAssetsLoading(false);
    }
  };

  // ========================================
  // DELETE BENEFICIARY (with orphaned asset handling)
  // ========================================
  const handleDelete = async (id, name) => {
    try {
      const token = localStorage.getItem('token');
      
      // First, check if deletion would orphan assets
      const checkResponse = await axios.delete(`${process.env.REACT_APP_API_URL}/api/beneficiaries/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        data: { confirmed: false }
      }).catch(err => err.response);
      
      // If there are orphaned assets, ask for confirmation
      if (checkResponse.status === 409 && checkResponse.data.orphanedAssets) {
        const assetList = checkResponse.data.orphanedAssets.map(a => `  - ${a.name} (${a.type})`).join('\n');
        const confirmed = window.confirm(
          `${checkResponse.data.message}\n\nAffected assets:\n${assetList}\n\nThese assets will be reassigned to your Global Next of Kin. Continue?`
        );
        
        if (confirmed) {
          // Confirm deletion with reassignment
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/beneficiaries/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` },
            data: { confirmed: true }
          });
          alert('Beneficiary deleted. Affected assets reassigned to Global Next of Kin.');
        } else {
          return;
        }
      } else if (checkResponse.status === 200) {
        // No orphaned assets, deletion successful
        alert('Beneficiary deleted successfully');
      } else {
        throw new Error(checkResponse.data?.error || 'Delete failed');
      }
      
      // Refresh the list
      fetchBeneficiaries();
      
      // Clear selection if the deleted beneficiary was selected
      if (selectedBeneficiary?.id === id) {
        setSelectedBeneficiary(null);
        setBeneficiaryAssets([]);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete beneficiary');
    }
  };

  // ========================================
  // GET ASSET ICON BASED ON TYPE
  // ========================================
  const getAssetIcon = (type) => {
    const icons = {
      bank_account: '/images/assets/asset-bank.png',
      real_estate: '/images/assets/asset-realestate.png',
      vehicle: '/images/assets/asset-vehicle.png',
      investment: '/images/assets/asset-investment.png',
      cryptocurrency: '/images/assets/asset-crypto.png',
      will: '/images/assets/asset-will.png',
      document: '/images/assets/asset-document.png',
      digital_asset: '/images/assets/asset-digital.png',
      other: '/images/assets/asset-other.png'
    };
    return icons[type] || '/images/assets/asset-other.png';
  };

  // ========================================
  // GET RELATIONSHIP ICON
  // ========================================
  const getRelationshipIcon = (relationship) => {
    const icons = {
      spouse: '💑',
      child: '👶',
      parent: '👨‍👩‍👧',
      sibling: '👥',
      friend: '🤝',
      lawyer: '⚖️',
      advisor: '📊',
      other: '🔗'
    };
    return icons[relationship] || '🔗';
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', flexDirection: 'column' }}>
      
      {/* ======================================== */}
      {/* MAIN CONTENT - Padded container */}
      {/* ======================================== */}
      <div style={{ padding: '20px', flex: 1 }}>
        
        {/* ======================================== */}
        {/* HEADER SECTION */}
        {/* ======================================== */}
        <div className="beneficiaries-header">
          <h1 className="beneficiaries-title">External Beneficiaries</h1>
          <div className="beneficiaries-actions">
            <Link to="/dashboard" className="back-btn">← Back to Dashboard</Link>
            <button onClick={() => setShowForm(!showForm)} className="add-btn">
              {showForm ? 'Cancel' : '+ Add Beneficiary'}
            </button>
          </div>
        </div>

        {/* ======================================== */}
        {/* ERROR MESSAGE */}
        {/* ======================================== */}
        {error && <div className="error-message">{error}</div>}

        {/* ======================================== */}
        {/* ADD BENEFICIARY FORM */}
        {/* ======================================== */}
        {showForm && (
          <div className="form-card">
            <h2 className="form-title">➕ Add External Beneficiary</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  placeholder="Enter full name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  placeholder="email@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0244XXXXXX"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Relationship</label>
                <select
                  name="relationship"
                  value={formData.relationship}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="spouse">💑 Spouse</option>
                  <option value="child">👶 Child</option>
                  <option value="parent">👨‍👩‍👧 Parent</option>
                  <option value="sibling">👥 Sibling</option>
                  <option value="friend">🤝 Friend</option>
                  <option value="lawyer">⚖️ Lawyer</option>
                  <option value="advisor">📊 Financial Advisor</option>
                  <option value="other">🔗 Other</option>
                </select>
              </div>
              <button type="submit" className="submit-btn" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Beneficiary'}
              </button>
            </form>
          </div>
        )}

        {/* ======================================== */}
        {/* MAIN CONTENT GRID - Beneficiaries + Assets */}
        {/* ======================================== */}
        {loading ? (
          <div className="loading-state">Loading beneficiaries...</div>
        ) : (
          <div className="beneficiaries-grid">
            
            {/* LEFT COLUMN: Beneficiaries List */}
            <div className="beneficiaries-list">
              <h2 className="section-title">👥 Your External Beneficiaries</h2>
              {beneficiaries.length === 0 ? (
                <div className="empty-state">
                  <p>No external beneficiaries yet.</p>
                  <p>Add beneficiaries who will receive specific assets.</p>
                </div>
              ) : (
                beneficiaries.map((beneficiary) => (
                  <div
                    key={beneficiary.id}
                    className={`beneficiary-card ${selectedBeneficiary?.id === beneficiary.id ? 'selected' : ''}`}
                    onClick={() => handleViewAssets(beneficiary)}
                  >
                    <div className="beneficiary-header">
                      <span className="beneficiary-name">{beneficiary.name}</span>
                      <span className="asset-count-badge">{beneficiary.assetCount} assets</span>
                    </div>
                    <div className="beneficiary-details">
                      <span className="beneficiary-email">📧 {beneficiary.email}</span>
                      {beneficiary.phone && <span className="beneficiary-phone">📞 {beneficiary.phone}</span>}
                      <span className="beneficiary-relationship">
                        {getRelationshipIcon(beneficiary.relationship)} {beneficiary.relationship}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(beneficiary.id, beneficiary.name);
                      }}
                      className="delete-btn"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* RIGHT COLUMN: Assets for Selected Beneficiary */}
            {selectedBeneficiary && (
              <div className="assets-list">
                <h2 className="section-title">
                  📦 Assets for {selectedBeneficiary.name}
                </h2>
                {assetsLoading ? (
                  <div className="loading-state">Loading assets...</div>
                ) : beneficiaryAssets.length === 0 ? (
                  <div className="empty-state">
                    <p>No assets assigned to this beneficiary.</p>
                    <p>Add this beneficiary when creating an asset.</p>
                  </div>
                ) : (
                  beneficiaryAssets.map((asset) => (
                    <div
                      key={asset.id}
                      className="asset-card"
                      onClick={() => navigate(`/asset/${asset.id}`)}
                    >
                      <img src={getAssetIcon(asset.assetType)} alt="" className="asset-icon-img" />
                      <div className="asset-info">
                        <div className="asset-header">
                          <span className="asset-name">{asset.assetName}</span>
                          <span className="asset-type">{asset.assetType?.replace('_', ' ')}</span>
                        </div>
                        <p className="asset-date">
                          Added: {new Date(asset.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
          </div>
        )}
      </div>

      {/* ======================================== */}
      {/* FOOTER - Full width */}
      {/* ======================================== */}
      <Footer />

      {/* ======================================== */}
      {/* GLOBAL STYLES - Fully Responsive */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        /* ======================================== */
        /* HEADER STYLES */
        /* ======================================== */
        .beneficiaries-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1200px;
          margin: 0 auto 30px;
          width: 100%;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .beneficiaries-title {
          font-size: 28px;
          color: #1A1A1A;
          margin: 0;
        }
        
        .beneficiaries-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        
        .back-btn {
          padding: 10px 20px;
          background: #1A1A1A;
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s;
        }
        
        .back-btn:hover {
          background: #333;
        }
        
        .add-btn {
          padding: 10px 20px;
          background: #FFD700;
          color: #1A1A1A;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
        }
        
        .add-btn:hover {
          background: #B8860B;
        }
        
        /* ======================================== */
        /* ERROR MESSAGE */
        /* ======================================== */
        .error-message {
          max-width: 1200px;
          margin: 0 auto 20px;
          background: #fee2e2;
          border: 1px solid #ef4444;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
        }
        
        /* ======================================== */
        /* FORM CARD */
        /* ======================================== */
        .form-card {
          background: white;
          padding: 25px;
          border-radius: 12px;
          margin-bottom: 30px;
          max-width: 500px;
          margin: 0 auto 30px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
        }
        
        .form-title {
          font-size: 20px;
          color: #FFD700;
          margin-bottom: 20px;
        }
        
        .form-group {
          margin-bottom: 16px;
        }
        
        .form-label {
          display: block;
          font-size: 14px;
          font-weight: bold;
          color: #1A1A1A;
          margin-bottom: 6px;
        }
        
        .form-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          box-sizing: border-box;
          transition: border-color 0.3s;
        }
        
        .form-input:focus {
          outline: none;
          border-color: #FFD700;
        }
        
        .submit-btn {
          width: 100%;
          padding: 12px;
          background: #FFD700;
          color: #1A1A1A;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
          margin-top: 10px;
        }
        
        .submit-btn:hover {
          background: #B8860B;
        }
        
        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* ======================================== */
        /* MAIN GRID - Responsive */
        /* ======================================== */
        .beneficiaries-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .beneficiaries-list {
          background: white;
          padding: 25px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .assets-list {
          background: white;
          padding: 25px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .section-title {
          font-size: 18px;
          color: #2E7D32;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #FFD700;
          display: inline-block;
        }
        
        /* ======================================== */
        /* EMPTY STATE */
        /* ======================================== */
        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
          background: #F5F0E6;
          border-radius: 10px;
        }
        
        .loading-state {
          text-align: center;
          padding: 40px;
          color: #666;
        }
        
        /* ======================================== */
        /* BENEFICIARY CARD */
        /* ======================================== */
        .beneficiary-card {
          background: #F5F0E6;
          padding: 16px;
          border-radius: 10px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.3s;
          position: relative;
        }
        
        .beneficiary-card:hover {
          transform: translateX(5px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .beneficiary-card.selected {
          border-left: 4px solid #FFD700;
          background: #FFF8E1;
        }
        
        .beneficiary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .beneficiary-name {
          font-size: 16px;
          font-weight: bold;
          color: #1A1A1A;
        }
        
        .asset-count-badge {
          font-size: 11px;
          background: #2E7D32;
          color: white;
          padding: 3px 10px;
          border-radius: 20px;
          font-weight: bold;
        }
        
        .beneficiary-details {
          display: flex;
          flex-direction: column;
          gap: 5px;
          font-size: 13px;
          color: #666;
        }
        
        .beneficiary-email, .beneficiary-phone, .beneficiary-relationship {
          display: inline-block;
        }
        
        .delete-btn {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 5px 10px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 11px;
          cursor: pointer;
          opacity: 0.6;
          transition: all 0.3s;
        }
        
        .delete-btn:hover {
          opacity: 1;
          background: #b91c1c;
        }
        
        /* ======================================== */
        /* ASSET CARD */
        /* ======================================== */
        .asset-card {
          display: flex;
          align-items: center;
          gap: 15px;
          background: #F5F0E6;
          padding: 12px;
          border-radius: 10px;
          margin-bottom: 10px;
          cursor: pointer;
          transition: all 0.3s;
        }
        
        .asset-card:hover {
          background: #E8E0D0;
          transform: translateX(5px);
        }
        
        .asset-icon-img {
          width: 45px;
          height: 45px;
          object-fit: contain;
        }
        
        .asset-info {
          flex: 1;
        }
        
        .asset-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 5px;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .asset-name {
          font-size: 14px;
          font-weight: bold;
          color: #1A1A1A;
        }
        
        .asset-type {
          font-size: 11px;
          color: #2E7D32;
          text-transform: capitalize;
          background: rgba(46,125,50,0.1);
          padding: 2px 8px;
          border-radius: 20px;
        }
        
        .asset-date {
          font-size: 11px;
          color: #999;
          margin: 0;
        }
        
        /* ======================================== */
        /* RESPONSIVE BREAKPOINTS */
        /* ======================================== */
        
        @media (max-width: 1024px) {
          .beneficiaries-grid {
            gap: 20px;
          }
        }
        
        @media (max-width: 768px) {
          .beneficiaries-header {
            flex-direction: column;
            text-align: center;
            margin-bottom: 20px;
          }
          
          .beneficiaries-title {
            font-size: 24px;
          }
          
          .beneficiaries-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .beneficiary-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .delete-btn {
            position: relative;
            top: auto;
            right: auto;
            margin-top: 12px;
            width: 100%;
            text-align: center;
          }
          
          .asset-card {
            flex-direction: column;
            text-align: center;
          }
          
          .asset-header {
            justify-content: center;
          }
        }
        
        @media (max-width: 480px) {
          .beneficiaries-title {
            font-size: 20px;
          }
          
          .beneficiaries-actions {
            flex-direction: column;
            width: 100%;
          }
          
          .back-btn, .add-btn {
            width: 100%;
            text-align: center;
          }
          
          .form-card {
            padding: 20px;
            margin: 0 0 20px 0;
          }
          
          .beneficiaries-list, .assets-list {
            padding: 20px;
          }
          
          .section-title {
            font-size: 16px;
          }
        }
        
      `}} />
    </div>
  );
};

export default Beneficiaries;